import type { Business } from '@/modules/dashboard/dashboardTypes';
import { Checkmark } from '@/resources/assets/images';
import Loader, { Status } from '@/components/Loader/Loader';
import pluralize from 'pluralize';
import { Dialog } from '@/components/atoms/Dialog';
import { ChangeEvent, useMemo, useState } from 'react';

import { format } from 'date-fns';
import { Select } from '@/components/atoms/Select';
import type { User } from '@/modules/user/userTypes';
import Button from '@/components/Button/newButtons';
import type {
	DashboardDeleteBusinessReminderProps,
	DashboardSetBusinessReminderProps
} from '@/modules/dashboard/dashboardActions';
import { MONTHS, DAYS, HOURS } from '../../constants/Date';

export type ReminderModalProps = {
	hasReminderTaskSet?: NonNullable<
		Business['reminder']
	>['hasReminderTaskSet'];
	reminderTriggerDate: NonNullable<
		Business['reminder']
	>['reminderTriggerDate'];
	reminderText: NonNullable<Business['reminder']>['reminderText'];
	businessId: Business['_id'];
	businessName: Business['name'];
	listId: Business['listId'];
	userEmail: User['email'];
	userTimeZone: User['timeZone'];
	originalNote: Business['note'];
	isOpen: boolean;
	onCloseModal: VoidFunction;
	isTaskLoading: boolean;
	isTaskError: boolean;
	isTaskSuccess: boolean;
	onResetReminder: VoidFunction;
	onDeleteReminder: (props: DashboardDeleteBusinessReminderProps) => void;
	onSetReminder: (props: DashboardSetBusinessReminderProps) => void;
};

export const ReminderModal = ({
	hasReminderTaskSet = false,
	reminderTriggerDate,
	reminderText,
	businessId,
	businessName,
	listId,
	originalNote,
	userEmail,
	userTimeZone,
	isTaskLoading,
	isTaskError,
	isTaskSuccess,
	isOpen,
	onCloseModal,
	onDeleteReminder,
	onResetReminder,
	onSetReminder
}: ReminderModalProps) => {
	const [noteText, setNoteText] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [reminderDate, setReminderDate] = useState({
		month: 0,
		day: 0,
		hour: 0
	});

	const handleUpdateMonthValue = (month: string) => {
		setReminderDate(state => ({
			...state,
			month: Number(month)
		}));
	};

	const handleUpdateDayValue = (day: string) => {
		setReminderDate(state => ({
			...state,
			day: Number(day)
		}));
	};

	const handleUpdateHourValue = (hour: string) => {
		setReminderDate(state => ({
			...state,
			hour: Number(hour)
		}));
	};

	const handleNodeTextChange = (value: string) => {
		setNoteText(value);
	};

	const handleCloseSetReminder = () => {
		onResetReminder();
		setNoteText('');
		setErrorMessage('');
		setReminderDate({
			month: 0,
			day: 0,
			hour: 0
		});
		onCloseModal();
	};

	const handleSubmitReminder = () => {
		if (hasReminderTaskSet) {
			onDeleteReminder({
				businessId,
				businessName,
				listId,
				userEmail
			});

			return handleCloseSetReminder();
		}

		const isReminderDateEmpty =
			reminderDate.month === 0 &&
			reminderDate.day === 0 &&
			reminderDate.hour === 0;

		if (isReminderDateEmpty) {
			return setErrorMessage("Reminder date can't be zero");
		}

		if (!noteText) {
			return setErrorMessage("Reminder note can't be empty");
		}

		const month = reminderDate.month * 30 * 24 * 60 * 60;
		const day = reminderDate.day * 24 * 60 * 60;
		const hour = reminderDate.hour * 60 * 60;

		const totalTimeInSeconds = month + day + hour;
		const triggerDate = totalTimeInSeconds + Date.now() / 1000;

		onSetReminder({
			note: noteText,
			triggerDate,
			noteHtml: noteText?.replace(/\r\n|\r|\n/g, '</br>'),
			originalNote,
			businessId,
			businessName,
			listId,
			userEmail
		});

		return handleCloseSetReminder();
	};

	const reminderDateFormmated = useMemo(
		() =>
			reminderTriggerDate
				? format(
						new Date(Number(reminderTriggerDate) * 1000),
						'MM/dd/yyyy'
					)
				: '',
		[reminderTriggerDate]
	);

	const reminderTimeFormmated = useMemo(
		() =>
			reminderTriggerDate
				? format(new Date(Number(reminderTriggerDate) * 1000), 'h:mm a')
				: '',
		[reminderTriggerDate]
	);

	const userTimezone = useMemo(
		() => (userTimeZone ? `${userTimeZone} time` : ''),
		[userTimeZone]
	);

	const isReminderDateGreaterThanOneDay = useMemo(
		() => reminderDate.day > 0 || reminderDate.month > 0,
		[reminderDate.day, reminderDate.month]
	);

	return (
		<Dialog.Root open={isOpen} onOpenChange={handleCloseSetReminder}>
			<Dialog.Content className="flex flex-col items-center justify-center gap-4">
				<Dialog.Title className="text-2xl text-dm-white">
					{hasReminderTaskSet ? 'Email' : 'Set an email'} reminder for{' '}
					<span className="text-orange">{businessName}</span>
				</Dialog.Title>

				<div className="flex w-full max-w-xs flex-col gap-4 pt-11">
					{hasReminderTaskSet && reminderTriggerDate ? (
						<p className="text-dm-white">
							{`Your reminder email will send on ${reminderDateFormmated} at ${reminderTimeFormmated} ${userTimezone}`}
						</p>
					) : (
						<ReminderDateSelector
							isReminderDateGreaterThanOneDay={
								isReminderDateGreaterThanOneDay
							}
							onUpdateMonthValue={handleUpdateMonthValue}
							onUpdateDayValue={handleUpdateDayValue}
							onUpdateHourValue={handleUpdateHourValue}
						/>
					)}

					<ReminderNotesContent
						isDisabled={Boolean(hasReminderTaskSet)}
						defaultValue={
							reminderText ||
							'No text was provided for the reminder'
						}
						onNoteTextChange={handleNodeTextChange}
					/>

					{errorMessage && (
						<small className="text-center text-dm-red">
							{errorMessage}
						</small>
					)}

					<Button
						onClick={handleSubmitReminder}
						size="large"
						fullWidth
						className="relative h-12 text-base"
					>
						{hasReminderTaskSet ? (
							'Delete This Reminder'
						) : (
							<>
								Set Your Reminder
								<span className="absolute -right-4 top-1/2 flex -translate-y-1/2 translate-x-full items-center justify-center">
									{isTaskLoading && (
										<Loader status={Status.LOADING} />
									)}

									{isTaskError && (
										<span className="failure-mark">X</span>
									)}

									{isTaskSuccess && (
										<Checkmark className="reminder-checkmark" />
									)}
								</span>
							</>
						)}
					</Button>
				</div>
			</Dialog.Content>
		</Dialog.Root>
	);
};

type ReminderDateSelectorProps = {
	isReminderDateGreaterThanOneDay: boolean;
	onUpdateMonthValue: (month: string) => void;
	onUpdateDayValue: (day: string) => void;
	onUpdateHourValue: (hour: string) => void;
};

const ReminderDateSelector = ({
	isReminderDateGreaterThanOneDay,
	onUpdateMonthValue,
	onUpdateDayValue,
	onUpdateHourValue
}: ReminderDateSelectorProps) => {
	const hoursWithZeroElement = [0, ...HOURS];
	const hoursOptionsArray = isReminderDateGreaterThanOneDay
		? hoursWithZeroElement
		: HOURS;

	return (
		<div className="flex flex-col gap-3">
			<header className="text-sm text-dm-white">
				How long before you would like to be reminded?
			</header>

			<section className="flex w-full items-center justify-center gap-2">
				<Select.Root onValueChange={onUpdateMonthValue}>
					<Select.Trigger>
						<Select.Value placeholder="0 months" />
					</Select.Trigger>

					<Select.Content>
						{MONTHS.map(month => (
							<Select.Item key={month} value={String(month)}>
								{pluralize('month', month, true)}
							</Select.Item>
						))}
					</Select.Content>
				</Select.Root>

				<Select.Root onValueChange={onUpdateDayValue}>
					<Select.Trigger>
						<Select.Value placeholder="0 days" />
					</Select.Trigger>

					<Select.Content>
						{DAYS.map(day => (
							<Select.Item key={day} value={String(day)}>
								{pluralize('day', day, true)}
							</Select.Item>
						))}
					</Select.Content>
				</Select.Root>

				<Select.Root onValueChange={onUpdateHourValue}>
					<Select.Trigger>
						<Select.Value placeholder="0 hours" />
					</Select.Trigger>

					<Select.Content>
						{hoursOptionsArray.map(hour => (
							<Select.Item key={hour} value={String(hour)}>
								{pluralize('hour', hour, true)}
							</Select.Item>
						))}
					</Select.Content>
				</Select.Root>
			</section>
		</div>
	);
};

type ReminderNotesContentProps = {
	isDisabled: boolean;
	defaultValue: string;
	onNoteTextChange: (value: string) => void;
};

const ReminderNotesContent = ({
	defaultValue,
	isDisabled,
	onNoteTextChange
}: ReminderNotesContentProps) => {
	const handleUpdateNodeText = (e: ChangeEvent<HTMLTextAreaElement>) => {
		if (isDisabled) return null;

		return onNoteTextChange(e.target.value);
	};

	return (
		<div className="w-full text-center text-dm-white">
			<textarea
				className="min-h-40 resize-none bg-dm-white text-dm-black"
				placeholder="Add a note for your reminder here."
				readOnly={isDisabled}
				disabled={isDisabled}
				defaultValue={isDisabled ? defaultValue : ''}
				onChange={handleUpdateNodeText}
			/>
		</div>
	);
};
